import { UUID } from "uuidv7"

export enum FileUserRoleTier {
  Owner = 0x0000000f,
  Admin = 0x00000003,
  Editor = 0x00000002,
  Viewer = 0x00000001,
  Base = 0x00000000,
}

export enum PermissionStatus {
  Unset = 0,
  Denied = 1,
  Allowed = 2,
  // Reserved = 3, // Currently unused, could be used for "override" in the future
}

// Permission bit positions (2 bits each, starting after the 8 bits for tier)
export enum Permission {
  AddingOwners = 8,
  RemovingOwners = 10,
  TransferringOwnership = 12,
  InSearches = 14,
  AddingAdministrators = 16,
  RemovingAdministrators = 18,
  AddingEditors = 20,
  RemovingEditors = 22,
  AddingViewers = 24,
  RemovingViewers = 26,
  SettingOfPublic = 28,
  Archiving = 30,
  PermanentlyDeleting = 32,
  ViewingPermissions = 34,
  Downloading = 36,
  ViewingHistory = 38,
  UpdatingMetadata = 40,
  Editing = 42,
  Moving = 44,
  Copying = 46,
  Uploading = 48,
  ViewingArchive = 50,
  AddingIndividualSharing = 52,
  Viewing = 54,
  Commenting = 56,
  MovingToArchive = 58,
  MovingFromArchive = 60,
  AgentFileModifications = 62,
  AgentMetadataModifications = 64,
  ModifyIndividualSharing = 66,
  AgentFileViewing = 68,
  AgentFileReading = 70,
  ViewingInExternalApplications = 72,
  ReadingInExternalApplications = 74,
  UpdatingInExternalApplications = 76,
  EditingInExternalApplications = 78,
  // Remaining 48 bits are unused
}

export class FileUserRole {
  /** stored as a uuid */
  _role: UUID

  constructor(role: string) {
    this._role = UUID.parse(role)
  }

  inner(): Readonly<string> {
    return this._role.toString()
  }

  tier(): FileUserRoleTier {
    const role = this.inner()
    return Number.parseInt(role.slice(role.length - 2, role.length), 16)
  }

  tierName() {
    switch (this.tier()) {
      case FileUserRoleTier.Owner: return "Owner"
      case FileUserRoleTier.Admin: return "Admin"
      case FileUserRoleTier.Editor: return "Editor"
      case FileUserRoleTier.Viewer: return "Viewer"
      case FileUserRoleTier.Base: return "Base"
      default: return "Unknown"
    }
  }

  displayText() {
    const parts = [this.tierName()]
    if (this.isCustomized())
      parts.push("(+ changes)")
    return parts.join(" ")
  }

  isTier(tier: FileUserRoleTier): boolean {
    return this.tier() === tier
  }

  isOwner(): boolean {
    return this.tier() === FileUserRoleTier.Owner
  }

  isAdmin(): boolean {
    return this.tier() === FileUserRoleTier.Admin
  }

  isEditor(): boolean {
    return this.tier() === FileUserRoleTier.Editor
  }

  isViewer(): boolean {
    return this.tier() === FileUserRoleTier.Viewer
  }

  isBase(): boolean {
    return this.tier() === FileUserRoleTier.Base
  }

  /**
   * If any of the permissions are not unset, i.e. this is a customized
   * role
   */
  isCustomized(): boolean {
    for (const permission of Object.values(Permission))
      if (this.getPermissionStatus(permission as Permission) !== PermissionStatus.Unset)
        return true
    return false
  }

  /**
   * Creates a new FileUserRole with just the Owner tier set
   * and no overrides specified. If you want to know whether a
   * specific action is allowed, `is` or `can` methods, as you
   * will only get Unset values in this object if queried
   * directly.
   * @returns A new FileUserRole with Owner tier
   */
  static newOwner(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Owner)
  }

  /**
   * Creates a new FileUserRole with just the Admin tier set
   * and no overrides specified. If you want to know whether a
   * specific action is allowed, `is` or `can` methods, as you
   * will only get Unset values in this object if queried
   * directly.
   * @returns A new FileUserRole with Admin tier
   */
  static newAdmin(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Admin)
  }

  /**
   * Creates a new FileUserRole with just the Editor tier set
   * and no overrides specified. If you want to know whether a
   * specific action is allowed, `is` or `can` methods, as you
   * will only get Unset values in this object if queried
   * directly.
   * @returns A new FileUserRole with Editor tier
   */
  static newEditor(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Editor)
  }

  /**
   * Creates a new FileUserRole with just the Viewer tier set
   * and no overrides specified. If you want to know whether a
   * specific action is allowed, `is` or `can` methods, as you
   * will only get Unset values in this object if queried
   * directly.
   * @returns A new FileUserRole with Viewer tier
   */
  static newViewer(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Viewer)
  }

  /**
   * Creates a new FileUserRole with just the Base tier set
   * and no overrides specified. If you want to know whether a
   * specific action is allowed, `is` or `can` methods, as you
   * will only get Unset values in this object if queried
   * directly.
   * @returns A new FileUserRole with Base tier
   */
  static newBase(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Base)
  }

  /**
   * Create a new FileUserRole with specific permissions
   * @param tier The base tier for the role
   * @param permissions A dictionary mapping permissions to their statuses
   * @returns A new FileUserRole instance with the specified permissions
   */
  static withPermissions(
    tier: FileUserRoleTier,
    permissions?: Record<Permission, PermissionStatus>,
  ): FileUserRole {
    // Create a new UUID with zeros
    const bytes = new Uint8Array(16)

    // Set the tier in the last byte
    bytes[15] = tier & 0xFF

    // Apply each permission
    for (const [permission, status] of Object.entries(permissions || {})) {
      const permBit = 128 - Number(permission)
      const byteIndex = Math.ceil(permBit / 8) - 1
      const bitOffset = (8 - (permBit % 8)) % 8

      // Clear the existing 2 bits and set the new status
      bytes[byteIndex] &= ~(0b11 << bitOffset)
      bytes[byteIndex] |= (status & 0b11) << bitOffset
    }

    // Create a UUID from the  bytes
    const uuid = UUID.ofInner(bytes)

    // Return a new FileUserRole
    return new FileUserRole(uuid.toString())
  }

  /**
   * Get the permission status for a specific permission
   * @param permission The permission bit position
   * @returns The status of the permission (Unset, Allowed, Denied)
   */
  getPermissionStatus(permission: Permission): PermissionStatus {
    // Get the raw bytes of the UUID
    const bytes = this._role.bytes

    // Calculate which byte contains our permission bits (little endian)
    // So our permission is read from the right, i.e. 128 - value
    // then the offset is inverted from the right
    const littleEndianPermission = 128 - permission
    const byteIndex = Math.ceil(littleEndianPermission / 8) - 1
    const bitOffset = (8 - (littleEndianPermission % 8)) % 8
    const status = (bytes[byteIndex] >> bitOffset) & 0b11 as PermissionStatus

    return status
  }

  /**
   * Check if a permission is allowed
   * @param permission The permission to check
   * @returns True if the permission is explicitly allowed
   */
  isPermissionAllowed(permission: Permission): boolean {
    const defaultRole = this.permissionsForMyTier()
    switch (this.getPermissionStatus(permission)) {
      case PermissionStatus.Allowed:
        return true
      case PermissionStatus.Denied:
        return false
      case PermissionStatus.Unset:
        // If the permission is unset, check the default role
        return defaultRole.getPermissionStatus(permission) === PermissionStatus.Allowed
      default:
        return false
    }
  }

  // Helper methods for each specific permission
  canAddOwners = () => this.isPermissionAllowed(Permission.AddingOwners)
  canRemoveOwners = () => this.isPermissionAllowed(Permission.RemovingOwners)
  canTransferOwnership = () => this.isPermissionAllowed(Permission.TransferringOwnership)
  canBeInSearches = () => this.isPermissionAllowed(Permission.InSearches)
  canAddAdministrators = () => this.isPermissionAllowed(Permission.AddingAdministrators)
  canRemoveAdministrators = () => this.isPermissionAllowed(Permission.RemovingAdministrators)
  canAddEditors = () => this.isPermissionAllowed(Permission.AddingEditors)
  canRemoveEditors = () => this.isPermissionAllowed(Permission.RemovingEditors)
  canAddViewers = () => this.isPermissionAllowed(Permission.AddingViewers)
  canRemoveViewers = () => this.isPermissionAllowed(Permission.RemovingViewers)
  canSetPublic = () => this.isPermissionAllowed(Permission.SettingOfPublic)
  canArchive = () => this.isPermissionAllowed(Permission.Archiving)
  canPermanentlyDelete = () => this.isPermissionAllowed(Permission.PermanentlyDeleting)
  canViewPermissions = () => this.isPermissionAllowed(Permission.ViewingPermissions)
  canDownload = () => this.isPermissionAllowed(Permission.Downloading)
  canViewHistory = () => this.isPermissionAllowed(Permission.ViewingHistory)
  canUpdateMetadata = () => this.isPermissionAllowed(Permission.UpdatingMetadata)
  canEdit = () => this.isPermissionAllowed(Permission.Editing)
  canMove = () => this.isPermissionAllowed(Permission.Moving)
  canCopy = () => this.isPermissionAllowed(Permission.Copying)
  canUpload = () => this.isPermissionAllowed(Permission.Uploading)
  canViewArchive = () => this.isPermissionAllowed(Permission.ViewingArchive)
  canAddIndividualSharing = () => this.isPermissionAllowed(Permission.AddingIndividualSharing)
  canView = () => this.isPermissionAllowed(Permission.Viewing)
  canComment = () => this.isPermissionAllowed(Permission.Commenting)
  canMoveToArchive = () => this.isPermissionAllowed(Permission.MovingToArchive)
  canMoveFromArchive = () => this.isPermissionAllowed(Permission.MovingFromArchive)
  canModifyFilesAsAgent = () => this.isPermissionAllowed(Permission.AgentFileModifications)
  canModifyMetadataAsAgent = () => this.isPermissionAllowed(Permission.AgentMetadataModifications)
  canModifyIndividualSharing = () => this.isPermissionAllowed(Permission.ModifyIndividualSharing)
  canViewFilesAsAgent = () => this.isPermissionAllowed(Permission.AgentFileViewing)
  canReadFilesAsAgent = () => this.isPermissionAllowed(Permission.AgentFileReading)
  canViewInExternalApplications = () => this.isPermissionAllowed(Permission.ViewingInExternalApplications)
  canReadInExternalApplications = () => this.isPermissionAllowed(Permission.ReadingInExternalApplications)
  canUpdateInExternalApplications = () => this.isPermissionAllowed(Permission.UpdatingInExternalApplications)
  canEditInExternalApplications = () => this.isPermissionAllowed(Permission.EditingInExternalApplications)

  /**
   * Aggregation over whether the user can add any type of user
   */
  allowsAddingUserToSharedFolder = () => {
    return this.canAddOwners() || this.canAddAdministrators() || this.canAddEditors() || this.canAddViewers()
  }

  /**
   * Overlays the "inner" (higher priority) role over our current one.
   *
   * We always take the inner's tier, whether higher or lower, but we
   * maintain the overrides. This allows us to easily override an override
   * by explicitly allowing it in the inner role.
   *
   * @param inner the lower role, i.e. the inner folder or file
   * @returns a new FileUserRole with the overlaid permissions
   */
  overlay(inner: FileUserRole): FileUserRole {
    // we take the inner's tier, and we merge both sides' permissions
    const overlaid = (perm: Permission) => {
      const theirs = inner.getPermissionStatus(perm)
      const mine = this.getPermissionStatus(perm)
      return theirs === PermissionStatus.Unset ? mine : theirs
    }

    return FileUserRole.withPermissions(inner.tier(), {
      [Permission.AddingOwners]: overlaid(Permission.AddingOwners),
      [Permission.RemovingOwners]: overlaid(Permission.RemovingOwners),
      [Permission.TransferringOwnership]: overlaid(Permission.TransferringOwnership),
      [Permission.InSearches]: overlaid(Permission.InSearches),
      [Permission.AddingAdministrators]: overlaid(Permission.AddingAdministrators),
      [Permission.RemovingAdministrators]: overlaid(Permission.RemovingAdministrators),
      [Permission.AddingEditors]: overlaid(Permission.AddingEditors),
      [Permission.RemovingEditors]: overlaid(Permission.RemovingEditors),
      [Permission.AddingViewers]: overlaid(Permission.AddingViewers),
      [Permission.RemovingViewers]: overlaid(Permission.RemovingViewers),
      [Permission.SettingOfPublic]: overlaid(Permission.SettingOfPublic),
      [Permission.Archiving]: overlaid(Permission.Archiving),
      [Permission.PermanentlyDeleting]: overlaid(Permission.PermanentlyDeleting),
      [Permission.ViewingPermissions]: overlaid(Permission.ViewingPermissions),
      [Permission.Downloading]: overlaid(Permission.Downloading),
      [Permission.ViewingHistory]: overlaid(Permission.ViewingHistory),
      [Permission.UpdatingMetadata]: overlaid(Permission.UpdatingMetadata),
      [Permission.Editing]: overlaid(Permission.Editing),
      [Permission.Moving]: overlaid(Permission.Moving),
      [Permission.Copying]: overlaid(Permission.Copying),
      [Permission.Uploading]: overlaid(Permission.Uploading),
      [Permission.ViewingArchive]: overlaid(Permission.ViewingArchive),
      [Permission.AddingIndividualSharing]: overlaid(Permission.AddingIndividualSharing),
      [Permission.ModifyIndividualSharing]: overlaid(Permission.ModifyIndividualSharing),
      [Permission.Viewing]: overlaid(Permission.Viewing),
      [Permission.Commenting]: overlaid(Permission.Commenting),
      [Permission.MovingToArchive]: overlaid(Permission.MovingToArchive),
      [Permission.MovingFromArchive]: overlaid(Permission.MovingFromArchive),
      [Permission.AgentMetadataModifications]: overlaid(Permission.AgentMetadataModifications),
      [Permission.AgentFileModifications]: overlaid(Permission.AgentFileModifications),
      [Permission.AgentFileViewing]: overlaid(Permission.AgentFileViewing),
      [Permission.AgentFileReading]: overlaid(Permission.AgentFileReading),
      [Permission.ViewingInExternalApplications]: overlaid(Permission.ViewingInExternalApplications),
      [Permission.ReadingInExternalApplications]: overlaid(Permission.ReadingInExternalApplications),
      [Permission.UpdatingInExternalApplications]: overlaid(Permission.UpdatingInExternalApplications),
      [Permission.EditingInExternalApplications]: overlaid(Permission.EditingInExternalApplications),
    })
  }

  static permissionsForTier(tier: FileUserRoleTier) {
    switch (tier) {
      case FileUserRoleTier.Owner:
        return FileUserRole.permissionsForOwner()
      case FileUserRoleTier.Admin:
        return FileUserRole.permissionsForAdmin()
      case FileUserRoleTier.Editor:
        return FileUserRole.permissionsForEditor()
      case FileUserRoleTier.Viewer:
        return FileUserRole.permissionsForViewer()
      case FileUserRoleTier.Base:
        return FileUserRole.permissionsForBase()
      default: {
        console.warn("Unknown tier, returning default base role", tier)
        return FileUserRole.permissionsForBase()
      }
    }
  }

  private permissionsForMyTier() {
    return FileUserRole.permissionsForTier(this.tier())
  }

  /**
   * Creates a FileUserRole with the default permissions for an Owner.
   * NOTE: you do NOT want to actually instantiate this class (that's)
   * why it is private. This is because this contains all overrides,
   * meaning if you overlay with this it will override everything.
   * Instead, you want the `newX` methods, which initialize the
   * permissions with Unset.
   *
   * @returns A new FileUserRole with Owner-level permissions
   */
  private static permissionsForOwner(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Owner, {
      [Permission.AddingOwners]: PermissionStatus.Allowed,
      [Permission.RemovingOwners]: PermissionStatus.Allowed,
      [Permission.TransferringOwnership]: PermissionStatus.Allowed,
      [Permission.InSearches]: PermissionStatus.Allowed,
      [Permission.AddingAdministrators]: PermissionStatus.Allowed,
      [Permission.RemovingAdministrators]: PermissionStatus.Allowed,
      [Permission.AddingEditors]: PermissionStatus.Allowed,
      [Permission.RemovingEditors]: PermissionStatus.Allowed,
      [Permission.AddingViewers]: PermissionStatus.Allowed,
      [Permission.RemovingViewers]: PermissionStatus.Allowed,
      [Permission.SettingOfPublic]: PermissionStatus.Allowed,
      [Permission.Archiving]: PermissionStatus.Allowed,
      [Permission.PermanentlyDeleting]: PermissionStatus.Allowed,
      [Permission.ViewingPermissions]: PermissionStatus.Allowed,
      [Permission.Downloading]: PermissionStatus.Allowed,
      [Permission.ViewingHistory]: PermissionStatus.Allowed,
      [Permission.UpdatingMetadata]: PermissionStatus.Allowed,
      [Permission.Editing]: PermissionStatus.Allowed,
      [Permission.Moving]: PermissionStatus.Allowed,
      [Permission.Copying]: PermissionStatus.Allowed,
      [Permission.Uploading]: PermissionStatus.Allowed,
      [Permission.ViewingArchive]: PermissionStatus.Allowed,
      [Permission.AddingIndividualSharing]: PermissionStatus.Allowed,
      [Permission.ModifyIndividualSharing]: PermissionStatus.Allowed,
      [Permission.Viewing]: PermissionStatus.Allowed,
      [Permission.Commenting]: PermissionStatus.Allowed,
      [Permission.MovingToArchive]: PermissionStatus.Allowed,
      [Permission.MovingFromArchive]: PermissionStatus.Allowed,
      [Permission.AgentMetadataModifications]: PermissionStatus.Allowed,
      [Permission.AgentFileModifications]: PermissionStatus.Allowed,
      [Permission.AgentFileViewing]: PermissionStatus.Allowed,
      [Permission.AgentFileReading]: PermissionStatus.Allowed,
      [Permission.ViewingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.ReadingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.UpdatingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.EditingInExternalApplications]: PermissionStatus.Allowed,
    })
  }

  /**
   * Creates a FileUserRole with the default permissions for an Admin.
   * NOTE: you do NOT want to actually instantiate this class (that's)
   * why it is private. This is because this contains all overrides,
   * meaning if you overlay with this it will override everything.
   * Instead, you want the `newX` methods, which initialize the
   * permissions with Unset.
   *
   * @returns A new FileUserRole with Admin-level permissions
   */
  private static permissionsForAdmin(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Admin, {
      [Permission.AddingOwners]: PermissionStatus.Denied,
      [Permission.RemovingOwners]: PermissionStatus.Denied,
      [Permission.TransferringOwnership]: PermissionStatus.Denied,
      [Permission.InSearches]: PermissionStatus.Allowed,
      [Permission.AddingAdministrators]: PermissionStatus.Allowed,
      [Permission.RemovingAdministrators]: PermissionStatus.Allowed,
      [Permission.AddingEditors]: PermissionStatus.Allowed,
      [Permission.RemovingEditors]: PermissionStatus.Allowed,
      [Permission.AddingViewers]: PermissionStatus.Allowed,
      [Permission.RemovingViewers]: PermissionStatus.Allowed,
      [Permission.SettingOfPublic]: PermissionStatus.Allowed,
      [Permission.Archiving]: PermissionStatus.Allowed,
      [Permission.PermanentlyDeleting]: PermissionStatus.Allowed,
      [Permission.ViewingPermissions]: PermissionStatus.Allowed,
      [Permission.Downloading]: PermissionStatus.Allowed,
      [Permission.ViewingHistory]: PermissionStatus.Allowed,
      [Permission.UpdatingMetadata]: PermissionStatus.Allowed,
      [Permission.Editing]: PermissionStatus.Allowed,
      [Permission.Moving]: PermissionStatus.Allowed,
      [Permission.Copying]: PermissionStatus.Allowed,
      [Permission.Uploading]: PermissionStatus.Allowed,
      [Permission.ViewingArchive]: PermissionStatus.Allowed,
      [Permission.AddingIndividualSharing]: PermissionStatus.Allowed,
      [Permission.ModifyIndividualSharing]: PermissionStatus.Allowed,
      [Permission.Viewing]: PermissionStatus.Allowed,
      [Permission.Commenting]: PermissionStatus.Allowed,
      [Permission.MovingToArchive]: PermissionStatus.Allowed,
      [Permission.MovingFromArchive]: PermissionStatus.Allowed,
      [Permission.AgentMetadataModifications]: PermissionStatus.Allowed,
      [Permission.AgentFileModifications]: PermissionStatus.Allowed,
      [Permission.AgentFileViewing]: PermissionStatus.Allowed,
      [Permission.AgentFileReading]: PermissionStatus.Allowed,
      [Permission.ViewingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.ReadingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.UpdatingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.EditingInExternalApplications]: PermissionStatus.Allowed,
    })
  }

  /**
   * Creates a FileUserRole with the default permissions for an Editor.
   * NOTE: you do NOT want to actually instantiate this class (that's)
   * why it is private. This is because this contains all overrides,
   * meaning if you overlay with this it will override everything.
   * Instead, you want the `newX` methods, which initialize the
   * permissions with Unset.
   *
   * @returns A new FileUserRole with Editor-level permissions
   */
  private static permissionsForEditor(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Editor, {
      [Permission.AddingOwners]: PermissionStatus.Denied,
      [Permission.RemovingOwners]: PermissionStatus.Denied,
      [Permission.TransferringOwnership]: PermissionStatus.Denied,
      [Permission.InSearches]: PermissionStatus.Allowed,
      [Permission.AddingAdministrators]: PermissionStatus.Denied,
      [Permission.RemovingAdministrators]: PermissionStatus.Denied,
      [Permission.AddingEditors]: PermissionStatus.Denied,
      [Permission.RemovingEditors]: PermissionStatus.Denied,
      [Permission.AddingViewers]: PermissionStatus.Denied,
      [Permission.RemovingViewers]: PermissionStatus.Denied,
      [Permission.SettingOfPublic]: PermissionStatus.Denied,
      [Permission.Archiving]: PermissionStatus.Allowed,
      [Permission.PermanentlyDeleting]: PermissionStatus.Denied,
      [Permission.ViewingPermissions]: PermissionStatus.Allowed,
      [Permission.Downloading]: PermissionStatus.Allowed,
      [Permission.ViewingHistory]: PermissionStatus.Allowed,
      [Permission.UpdatingMetadata]: PermissionStatus.Allowed,
      [Permission.Editing]: PermissionStatus.Allowed,
      [Permission.Moving]: PermissionStatus.Allowed,
      [Permission.Copying]: PermissionStatus.Allowed,
      [Permission.Uploading]: PermissionStatus.Allowed,
      [Permission.ViewingArchive]: PermissionStatus.Allowed,
      [Permission.AddingIndividualSharing]: PermissionStatus.Denied,
      [Permission.ModifyIndividualSharing]: PermissionStatus.Denied,
      [Permission.Viewing]: PermissionStatus.Allowed,
      [Permission.Commenting]: PermissionStatus.Allowed,
      [Permission.MovingToArchive]: PermissionStatus.Allowed,
      [Permission.MovingFromArchive]: PermissionStatus.Allowed,
      [Permission.AgentMetadataModifications]: PermissionStatus.Allowed,
      [Permission.AgentFileModifications]: PermissionStatus.Allowed,
      [Permission.AgentFileViewing]: PermissionStatus.Allowed,
      [Permission.AgentFileReading]: PermissionStatus.Allowed,
      [Permission.ViewingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.ReadingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.UpdatingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.EditingInExternalApplications]: PermissionStatus.Allowed,
    })
  }

  /**
   * Creates a FileUserRole with the default permissions for a Viewer.
   * NOTE: you do NOT want to actually instantiate this class (that's)
   * why it is private. This is because this contains all overrides,
   * meaning if you overlay with this it will override everything.
   * Instead, you want the `newX` methods, which initialize the
   * permissions with Unset.
   *
   * @returns A new FileUserRole with Viewer-level permissions
   */
  private static permissionsForViewer(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Viewer, {
      [Permission.AddingOwners]: PermissionStatus.Denied,
      [Permission.RemovingOwners]: PermissionStatus.Denied,
      [Permission.TransferringOwnership]: PermissionStatus.Denied,
      [Permission.InSearches]: PermissionStatus.Allowed,
      [Permission.AddingAdministrators]: PermissionStatus.Denied,
      [Permission.RemovingAdministrators]: PermissionStatus.Denied,
      [Permission.AddingEditors]: PermissionStatus.Denied,
      [Permission.RemovingEditors]: PermissionStatus.Denied,
      [Permission.AddingViewers]: PermissionStatus.Denied,
      [Permission.RemovingViewers]: PermissionStatus.Denied,
      [Permission.SettingOfPublic]: PermissionStatus.Denied,
      [Permission.Archiving]: PermissionStatus.Denied,
      [Permission.PermanentlyDeleting]: PermissionStatus.Denied,
      [Permission.ViewingPermissions]: PermissionStatus.Allowed,
      [Permission.Downloading]: PermissionStatus.Allowed,
      [Permission.ViewingHistory]: PermissionStatus.Allowed,
      [Permission.UpdatingMetadata]: PermissionStatus.Denied,
      [Permission.Editing]: PermissionStatus.Denied,
      [Permission.Moving]: PermissionStatus.Denied,
      [Permission.Copying]: PermissionStatus.Allowed,
      [Permission.Uploading]: PermissionStatus.Denied,
      [Permission.ViewingArchive]: PermissionStatus.Allowed,
      [Permission.AddingIndividualSharing]: PermissionStatus.Denied,
      [Permission.ModifyIndividualSharing]: PermissionStatus.Denied,
      [Permission.Viewing]: PermissionStatus.Allowed,
      [Permission.Commenting]: PermissionStatus.Allowed,
      [Permission.MovingToArchive]: PermissionStatus.Denied,
      [Permission.MovingFromArchive]: PermissionStatus.Denied,
      [Permission.AgentMetadataModifications]: PermissionStatus.Denied,
      [Permission.AgentFileModifications]: PermissionStatus.Denied,
      [Permission.AgentFileViewing]: PermissionStatus.Allowed,
      [Permission.AgentFileReading]: PermissionStatus.Allowed,
      [Permission.ViewingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.ReadingInExternalApplications]: PermissionStatus.Allowed,
      [Permission.UpdatingInExternalApplications]: PermissionStatus.Denied,
      [Permission.EditingInExternalApplications]: PermissionStatus.Denied,
    })
  }

  /**
   * Creates a FileUserRole with the default permissions for Base tier (all denied).
   * NOTE: you do NOT want to actually instantiate this class (that's)
   * why it is private. This is because this contains all overrides,
   * meaning if you overlay with this it will override everything.
   * Instead, you want the `newX` methods, which initialize the
   * permissions with Unset.
   *
   * @returns A new FileUserRole with Base-level permissions
   */
  private static permissionsForBase(): FileUserRole {
    return FileUserRole.withPermissions(FileUserRoleTier.Base, {
      [Permission.AddingOwners]: PermissionStatus.Denied,
      [Permission.RemovingOwners]: PermissionStatus.Denied,
      [Permission.TransferringOwnership]: PermissionStatus.Denied,
      [Permission.InSearches]: PermissionStatus.Denied,
      [Permission.AddingAdministrators]: PermissionStatus.Denied,
      [Permission.RemovingAdministrators]: PermissionStatus.Denied,
      [Permission.AddingEditors]: PermissionStatus.Denied,
      [Permission.RemovingEditors]: PermissionStatus.Denied,
      [Permission.AddingViewers]: PermissionStatus.Denied,
      [Permission.RemovingViewers]: PermissionStatus.Denied,
      [Permission.SettingOfPublic]: PermissionStatus.Denied,
      [Permission.Archiving]: PermissionStatus.Denied,
      [Permission.PermanentlyDeleting]: PermissionStatus.Denied,
      [Permission.ViewingPermissions]: PermissionStatus.Denied,
      [Permission.Downloading]: PermissionStatus.Denied,
      [Permission.ViewingHistory]: PermissionStatus.Denied,
      [Permission.UpdatingMetadata]: PermissionStatus.Denied,
      [Permission.Editing]: PermissionStatus.Denied,
      [Permission.Moving]: PermissionStatus.Denied,
      [Permission.Copying]: PermissionStatus.Denied,
      [Permission.Uploading]: PermissionStatus.Denied,
      [Permission.ViewingArchive]: PermissionStatus.Denied,
      [Permission.AddingIndividualSharing]: PermissionStatus.Denied,
      [Permission.ModifyIndividualSharing]: PermissionStatus.Denied,
      [Permission.Viewing]: PermissionStatus.Denied,
      [Permission.Commenting]: PermissionStatus.Denied,
      [Permission.MovingToArchive]: PermissionStatus.Denied,
      [Permission.MovingFromArchive]: PermissionStatus.Denied,
      [Permission.AgentMetadataModifications]: PermissionStatus.Denied,
      [Permission.AgentFileModifications]: PermissionStatus.Denied,
      [Permission.AgentFileViewing]: PermissionStatus.Denied,
      [Permission.AgentFileReading]: PermissionStatus.Denied,
      [Permission.ViewingInExternalApplications]: PermissionStatus.Denied,
      [Permission.ReadingInExternalApplications]: PermissionStatus.Denied,
      [Permission.UpdatingInExternalApplications]: PermissionStatus.Denied,
      [Permission.EditingInExternalApplications]: PermissionStatus.Denied,
    })
  }
}
